import { useFiniteAnimation } from '@lib/hooks/useAnimation'
import { ariaAttributes } from '@lib/utils/aria'
import type { WithTestId } from '@lib/utils/testid'
import classNames from 'classnames'
import type { ButtonHTMLAttributes } from 'react'
import { forwardRef, useState } from 'react'

import styles from './button.module.scss'
import { ButtonContent } from './content'
import { PRESETS } from './presets'
import type { CommonButtonProps } from './props'

/**
 * A button. Just a button. Not a link, for that @see LinkButton.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
    (
        {
            id,
            'data-testid': testId,
            analyticsProperties,
            animatePress = false,
            children,
            className,
            containerElementId,
            disabled,
            loading,
            name,
            onClick,
            placement,
            preset = 'primary',
            role = 'button',
            size = 'medium',
            style,
            title,
            type = 'button',
            ...restOfProps
        },
        ref,
    ) => {
        const presetStyles =
            PRESETS[preset as keyof typeof PRESETS] ?? PRESETS.primary

        const [animationRef, play, animationStatus] = useFiniteAnimation(
            'stepButtonPress',
            { resetTimeout: 200 },
        )

        const [executingOnClick, setExecutingOnClick] = useState(false)

        return (
            <button
                id={id?.toLowerCase().trim() ?? testId?.replace('.', '')}
                {...ariaAttributes(restOfProps)}
                className={classNames(
                    { [styles.disabled]: disabled },
                    styles.button,
                    presetStyles.button,
                    presetStyles[size],
                    (presetStyles as any)[animationStatus],
                    className,
                )}
                data-testid={testId}
                disabled={disabled || executingOnClick}
                name={name}
                onClick={async (e) => {
                    setExecutingOnClick(true)
                    try {
                        setTimeout(() => setExecutingOnClick(false), 1000)
                        if (animatePress) play()
                        await onClick?.(e)
                    } finally {
                        setExecutingOnClick(false)
                    }
                }}
                ref={ref}
                role={role}
                style={style}
                title={title}
                type={type}
            >
                <ButtonContent
                    ref={animationRef}
                    animationStatus={animationStatus}
                    loading={loading || executingOnClick}
                >
                    {children}
                </ButtonContent>
            </button>
        )
    },
)

export type ButtonProps = WithTestId<
    CommonButtonProps &
        Pick<
            ButtonHTMLAttributes<HTMLButtonElement>,
            | 'title'
            | 'children'
            | 'disabled'
            | 'name'
            | 'onClick'
            | 'role'
            | 'style'
            | 'type'
            | 'id'
        > & {
            /** Play special animation on press. True by default.  */
            animatePress?: boolean
        }
>
